import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styles from './Support.module.css';
import SearchInput from '../../components/SearchInput/SearchInput';
import Pill from '../../components/Pill/Pill';
import Avatar from '../../components/Avatar/Avatar';
import { api, mapConversation, uploadImage } from '../../lib/api';
import { usePoll } from '../../lib/usePoll';
import { hasUnseen, markSeen, onSeenChange } from '../../lib/supportSeen';

const Support = () => {
    const [conversations, setConversations] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    // Tapped photo, shown full-size — a screenshot in a chat bubble is
    // unreadable, and screenshots are most of what users send here.
    const [preview, setPreview] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const messagesRef = useRef(null);
    const fileRef = useRef(null);

    const loadConversations = useCallback(async () => {
        try {
            const { conversations: list } = await api('/admin/support');
            setConversations(list.map(mapConversation));
        } catch {
            setConversations([]);
        }
    }, []);

    useEffect(() => {
        loadConversations();
    }, [loadConversations]);

    // Support is a conversation with a person who is waiting. Nothing pushes to
    // the portal, so without this an admin watching this screen never sees a
    // reply arrive. usePoll pauses while the tab is hidden.
    usePoll(loadConversations, 15000);

    const filteredConversations = conversations.filter((conversation) =>
        conversation.name.toLowerCase().includes(search.toLowerCase())
    );

    const selected = conversations.find((conversation) => conversation.id === selectedId);
    // Which threads have said something this admin hasn't read. The server's
    // own flag only says a reply is still owed — it survives opening the chat
    // (and the poll below puts it straight back), so what has actually been
    // read is tracked here. Recomputed on the seen store's own event so the
    // dot goes the moment the thread is opened.
    const [unreadIds, setUnreadIds] = useState(() => new Set());
    useEffect(() => {
        const recompute = () =>
            setUnreadIds(
                new Set(
                    conversations
                        .filter((conversation) =>
                            hasUnseen(conversation.id, conversation.lastAt, conversation.waiting)
                        )
                        .map((conversation) => conversation.id)
                )
            );
        recompute();
        return onSeenChange(recompute);
    }, [conversations]);

    const unreadCount = unreadIds.size;

    // Opened from the notification bell: it links straight at a conversation,
    // which can only be selected once the list it lives in has loaded. The
    // parameter is cleared afterwards so it can't fight the admin's next click.
    const requestedId = searchParams.get('conversation');
    useEffect(() => {
        if (!requestedId) return;
        if (!conversations.some((conversation) => conversation.id === requestedId)) return;
        setSelectedId(requestedId);
        setSearchParams({}, { replace: true });
    }, [requestedId, conversations, setSearchParams]);

    // A thread is read from the bottom: the newest message is the one being
    // answered. Switching conversations jumps there outright; a message
    // arriving in the open one — sent here, or brought in by the poll — slides
    // down to it, so neither ever lands below the fold.
    const messageCount = selected?.messages.length ?? 0;
    const shownThread = useRef(null);
    useEffect(() => {
        const box = messagesRef.current;
        if (!box) return;
        const switched = shownThread.current !== selectedId;
        shownThread.current = selectedId;
        box.scrollTo({ top: box.scrollHeight, behavior: switched ? 'auto' : 'smooth' });
    }, [selectedId, messageCount]);

    // Everything in the open thread counts as read — including a reply that
    // lands while the admin is looking at it, which is why this follows the
    // conversation rather than only firing on the click.
    useEffect(() => {
        if (!selected) return;
        markSeen(selected.id, selected.lastAt);
    }, [selected]);

    const handleSelect = (conversation) => {
        setSelectedId(conversation.id);
        setDraft('');
        markSeen(conversation.id, conversation.lastAt);
    };

    const postMessage = async (body) => {
        setSending(true);
        try {
            const { conversation } = await api(`/admin/support/${selectedId}/messages`, {
                method: 'POST',
                body,
            });
            const mapped = mapConversation(conversation);
            setConversations((prev) => prev.map((entry) => (entry.id === selectedId ? mapped : entry)));
            setDraft('');
        } catch (err) {
            alert(err.message);
        } finally {
            setSending(false);
        }
    };

    const handleSend = async (event) => {
        event.preventDefault();
        const text = draft.trim();
        if (!text || sending) return;
        await postMessage({ text });
    };

    // A photo goes up as its own message, carrying whatever was already typed
    // as its caption — the same thing the app and the website do.
    const handleAttach = async (event) => {
        const file = event.target.files?.[0];
        // Clear the input either way, or picking the same file twice fires nothing.
        event.target.value = '';
        if (!file || sending) return;
        setSending(true);
        try {
            const url = await uploadImage(file);
            await postMessage({ text: draft.trim(), image: url });
        } catch (err) {
            alert(err.message);
        } finally {
            setSending(false);
        }
    };

    return (
        // Two panes side by side from lg up. Below that there isn't room for
        // both, so it becomes one pane at a time: the conversation list, or the
        // chat once one is picked (with a back button to return).
        <div className={`${styles.layout} d-flex gap-4`}>
            <aside
                className={`${styles.sidebar} ${selected ? styles.paneHiddenSm : ''} d-flex flex-column`}
            >
                <div className="p-4 pb-3">
                    <div className="d-flex align-items-center gap-3">
                        <h2 className={`${styles.panelTitle} mb-0`}>Conversations</h2>
                        {unreadCount > 0 && <span className={styles.newBadge}>{unreadCount} new</span>}
                    </div>

                    <div className="mt-3">
                        <SearchInput
                            className={styles.search}
                            placeholder="Search for users..."
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                        />
                    </div>
                </div>

                <div className="flex-grow-1 overflow-y-auto">
                    {filteredConversations.map((conversation, index) => (
                        <button
                            key={conversation.id}
                            type="button"
                            className={`${styles.conversation} ${conversation.id === selectedId ? styles.conversationActive : ''} d-flex align-items-center gap-3 w-100 p-3 px-4 text-start`}
                            onClick={() => handleSelect(conversation)}
                        >
                            <Avatar name={conversation.name} index={index} size={48} />
                            <div className="flex-grow-1 overflow-hidden">
                                <div className="d-flex align-items-center gap-2">
                                    <span className={styles.conversationName}>{conversation.name}</span>
                                    <Pill>{conversation.role}</Pill>
                                </div>
                                <div className={`${styles.preview} mt-1`}>{conversation.preview}</div>
                            </div>
                            {unreadIds.has(conversation.id) && <span className={styles.unreadDot} />}
                        </button>
                    ))}
                </div>
            </aside>

            <section
                className={`${styles.chatPanel} ${!selected ? styles.paneHiddenSm : ''} flex-grow-1 d-flex flex-column`}
            >
                {!selected ? (
                    <div className="flex-grow-1 d-flex flex-column align-items-center justify-content-center text-center p-4">
                        <h2 className={styles.emptyTitle}>No chat selected</h2>
                        <p className={`${styles.emptySubtitle} mt-2 mb-0`}>
                            Select a chat or conversation to<br />start responding to customers
                        </p>
                    </div>
                ) : (
                    <>
                        <div className={`${styles.chatHeader} d-flex align-items-center gap-3 p-3 p-md-4`}>
                            {/* Below lg the chat replaces the list, so there
                                has to be a way back to it. */}
                            <button
                                type="button"
                                className={`${styles.backButton} d-lg-none`}
                                aria-label="Back to conversations"
                                onClick={() => setSelectedId(null)}
                            >
                                <span aria-hidden="true">&lsaquo;</span>
                            </button>
                            <Avatar name={selected.name} index={0} size={56} />
                            <div className={styles.chatHeaderInfo}>
                                <div className="d-flex align-items-center gap-2">
                                    <span className={styles.chatName}>{selected.name}</span>
                                    <Pill>{selected.role}</Pill>
                                </div>
                                {selected.farm && <div className={styles.chatFarm}>{selected.farm}</div>}
                            </div>
                        </div>

                        <div ref={messagesRef} className={`${styles.messages} flex-grow-1 overflow-y-auto p-4`}>
                            {selected.messages.map((message) => (
                                <div
                                    key={message.id}
                                    className={`d-flex flex-column mb-3 ${message.from === 'admin' ? 'align-items-end' : 'align-items-start'}`}
                                >
                                    <div
                                        className={`${styles.bubble} ${message.from === 'admin' ? styles.bubbleOut : styles.bubbleIn} ${message.image ? styles.bubblePhoto : ''}`}
                                    >
                                        {message.image && (
                                            <button
                                                type="button"
                                                className={styles.photoButton}
                                                onClick={() => setPreview(message.image)}
                                                aria-label="View photo full size"
                                            >
                                                <img src={message.image} alt="" className={styles.photo} />
                                            </button>
                                        )}
                                        {message.text && (
                                            <span className={message.image ? styles.caption : ''}>
                                                {message.text}
                                            </span>
                                        )}
                                    </div>
                                    <span className={`${styles.messageTime} mt-2`}>{message.time}</span>
                                </div>
                            ))}
                        </div>

                        <form className="p-4 pt-0" onSubmit={handleSend}>
                            <div className={`${styles.sendBar} d-flex align-items-center gap-3 px-4`}>
                                <button
                                    type="button"
                                    className={styles.attachButton}
                                    onClick={() => fileRef.current?.click()}
                                    disabled={sending}
                                    title="Attach a photo"
                                    aria-label="Attach a photo"
                                >
                                    <span aria-hidden="true">&#128247;</span>
                                </button>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    className="d-none"
                                    onChange={handleAttach}
                                />
                                <input
                                    type="text"
                                    className={styles.sendInput}
                                    placeholder="Type a message..."
                                    value={draft}
                                    onChange={(event) => setDraft(event.target.value)}
                                />
                                <button
                                    type="submit"
                                    className={styles.sendButton}
                                    disabled={!draft.trim() || sending}
                                >
                                    Send
                                </button>
                            </div>
                        </form>
                    </>
                )}
            </section>

            {preview && (
                <div
                    className={`${styles.lightbox} d-flex align-items-center justify-content-center`}
                    onClick={() => setPreview(null)}
                    role="presentation"
                >
                    <img src={preview} alt="" className={styles.lightboxImage} />
                </div>
            )}
        </div>
    );
};

export default Support;
